import { useRef, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Footer, NavbarUser } from '../../containers';
import "./eventsadmin.css";
import api from "../../api/axios";
import { useTranslation } from 'react-i18next';

const EventsAdmin = (data) => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    // Footer berechnen
    const footerRef = useRef(null);
    const [footerHeight, setFooterHeight] = useState(0);
    // Navbar berechnen
    const [navbarHeight, setNavbarHeight] = useState(0);
    // Event Bild
    const [selectedFile, setSelectedFile] = useState(null);
    const [lokalpic, setLokalPic] = useState("");
    // Event Daten
    const [eventName, setEventName] = useState("");
    const [eventtype, setEventType] = useState("");
    const [lokal, setLokal] = useState("");
    const [adresse, setAdresse] = useState("");
    const [description, setDescription] = useState("");
    const [datum, setDatum] = useState("");
    const [maxplaces, setMaxPlaces] = useState("");
    const [citydistrict, setCityDistrict] = useState("");
    const [timeslot, setTimeSlot] = useState("");
    const [bookedplaces, setBookedPlaces] = useState("");
    const [price, setPrice] = useState("");
    const [isFree, setIsFree] = useState(false);
    const [matchedEvents, setMatchedEvents] = useState([]);

    // State
    const [selectedWebsiteFile, setSelectedWebsiteFile] = useState(null);
    const [selectedWebsiteVideo, setSelectedWebsiteVideo] = useState(null);
    const [websitePicDescription, setWebsitePicDescription] = useState("");
    const [pictureSrc, setPictureSrc] = useState("");
    const [eventTypeDropdownOpen, setEventTypeDropdownOpen] = useState(false);

    useEffect(() => {
        // Footer height calculation
        if (footerRef.current) {
            setFooterHeight(footerRef.current.clientHeight);
        }

        // Navbar height calculation
        const updateNavbarHeight = () => {
            const navbarElement = document.querySelector('.navbarHomepage') || document.querySelector('.navbarUser');
            if (navbarElement) {
                setNavbarHeight(navbarElement.offsetHeight);
            }
        };

        updateNavbarHeight(); // Initial call
        window.addEventListener('resize', updateNavbarHeight); // Add listener for dynamic updates

        const fetchPicture = async () => {
            try {
                const pictureId = 22; // 🔹 hardcoded for now, can vary per site later
                const response = await api.get(`/user/getwebsitepicture/${pictureId}`);
                setPictureSrc(response.data.picture); // base64 string
            } catch (err) {
                console.error("Error fetching picture:", err);
            }
        };

        fetchPicture();

        // Scroll to top on page load
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

        const fetchMatchedEvents = async () => {
            try {
                const response = await api.get("/user/getuniquematchedevents");

                const filteredMatchingEvents = response.data.filter(event => {
                    const eventDate = new Date(event.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return eventDate >= today;
                });
                setMatchedEvents(filteredMatchingEvents);
            } catch (err) {
                console.error("Error loading matched events:", err);
            }
        };

        fetchMatchedEvents();

        return () => window.removeEventListener('resize', updateNavbarHeight);
    }, []);

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleFileUpload = async () => {
        const missingFields = [];
        if (!eventName.trim()) missingFields.push("Event Name");
        if (!eventtype) missingFields.push("Event Type");
        if (!citydistrict.trim()) missingFields.push("City District");
        if (!description.trim()) missingFields.push("Description");
        if (!datum) missingFields.push("Date");
        if (!timeslot.trim()) missingFields.push("Time");
        if (!maxplaces) missingFields.push("Max Places");
        if (!isFree && !price) missingFields.push("Price");

        if (missingFields.length > 0) {
            alert(`Please fill out the following fields:\n- ${missingFields.join("\n- ")}`);
            return;
        }

        if (!selectedFile) {
            alert("Please select a file.");
            return;
        }

        const formData = new FormData();
        formData.append("lokalpic", selectedFile);
        formData.append("eventName", eventName);
        formData.append("lokal", lokal);
        formData.append("eventtype", eventtype);
        formData.append("adresse", adresse);
        formData.append("description", description);
        formData.append("datum", datum);
        formData.append("maxplaces", maxplaces || 0);
        formData.append("citydistrict", citydistrict);
        formData.append("timeslot", timeslot);
        formData.append("bookedplaces", 0);
        formData.append("price", isFree ? 0 : (price || 0));

        try {
            const response = await api.post(
                "/user/uploadeventdata",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" }
                }
            );

            alert("Event created successfully!");

            // Show uploaded image immediately
            const reader = new FileReader();
            reader.onloadend = () => {
                setLokalPic(reader.result); // "data:image/...;base64,..."
            };
            reader.readAsDataURL(selectedFile);

        } catch (error) {
            console.error("Error creating event:", error);
            alert("Upload failed");
        }
    };

    const getTomorrowDate = () => {
        const today = new Date();
        today.setDate(today.getDate() + 1); // Move to next day
        return today.toISOString().split("T")[0]; // Format to YYYY-MM-DD
    };

    // File change handler
    const handleWebsiteFileChange = (e) => {
        setSelectedWebsiteFile(e.target.files[0]);
    };

    // Upload handler
    const handleWebsiteFileUpload = async () => {
        if (!selectedWebsiteFile) {
            alert("Please select a file.");
            return;
        }

        const formData = new FormData();
        formData.append("websitepic", selectedWebsiteFile);
        formData.append("description", websitePicDescription);

        try {
            await api.post(
                "/user/uploadwebsitepicture",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" }
                }
            );
            alert("Website picture uploaded successfully!");
            setSelectedWebsiteFile(null);
            setWebsitePicDescription("");
        } catch (error) {
            console.error("Error uploading website picture:", error);
            alert("Upload failed");
        }
    };

    const handleWebsiteVideoChange = (e) => {
        setSelectedWebsiteVideo(e.target.files[0]);
    };

    const handleWebsiteVideoUpload = async () => {
        if (!selectedWebsiteVideo) {
            alert("Please select a video.");
            return;
        }

        const formData = new FormData();
        formData.append("websitevideo", selectedWebsiteVideo);

        try {
            await api.post(
                "/user/uploadwebsitevideo",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            alert("Website video uploaded successfully!");
            setSelectedWebsiteVideo(null);
        } catch (error) {
            console.error("Error uploading website video:", error);
            alert("Upload failed");
        }
    };

    const updateEventRow = async (event) => {
        try {
            if (event.groupid) {
                // Bar night event — update via bookedeventsgroups
                await api.post("/user/updatematchedgroup", {
                    groupid: event.groupid,
                    lokal: event.lokal,
                    address: event.address
                });
            } else {
                // Pub crawl event — update directly in eventsdata
                await api.post("/user/updatepubcrawlevent", {
                    eventid: event.eventid,
                    lokal: event.lokal,
                    address: event.address
                });
            }

            alert("Event updated successfully!");
        } catch (err) {
            console.error(err);
            alert("Update failed");
        }
    };

    const backToHomepage = () => {
        navigate('/');
    };

    const formatEventDate = (dateString) => {
        const options = {
            timeZone: 'Europe/Berlin', // Change to your desired time zone
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        };
        const dateTimeFormat = new Intl.DateTimeFormat('de-DE', options);
        const formattedDate = dateTimeFormat.format(new Date(dateString));
        return formattedDate; // Format as DD.MM.YYYY
    };

    return (
        <div className="wrapper-admin" style={{ backgroundImage: pictureSrc ? `url(${pictureSrc})` : "none", }}>
            <>
                <NavbarUser />
                <div className="admin-site" id="admin" style={{ minHeight: `calc(100vh - ${footerHeight}px)` }}>
                    <div className="admin-content-parent" style={{ marginTop: `${navbarHeight + 1}px` }}>
                        <h2>{t('EventsAdminTitle')}</h2>
                        <h1>{t('EventsAdminTitle2')}</h1>
                        <div className="admin-content-child">
                            <label>{t('EventsAdminEventName')} </label>
                            <input type="text" name="eventName" value={eventName} onChange={(e) => setEventName(e.target.value)} />
                            <label>{t('EventsAdminEventType')} </label>
                            <div className="admin-custom-dropdown">
                                <button
                                    type="button"
                                    className="admin-dropdown-trigger"
                                    onClick={() => setEventTypeDropdownOpen(!eventTypeDropdownOpen)}
                                >
                                    {eventtype === "pub crawl" ? "Pub Crawl" : eventtype === "bar night" ? "Bar Night" : "Select Event Type"}
                                    <span className="admin-dropdown-arrow">▾</span>
                                </button>
                                {eventTypeDropdownOpen && (
                                    <div className="admin-dropdown-menu">
                                        {[{ value: "pub crawl", label: "Pub Crawl" }, { value: "bar night", label: "Bar Night" }].map((opt) => (
                                            <div
                                                key={opt.value}
                                                className={`admin-dropdown-item ${eventtype === opt.value ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setEventType(opt.value);
                                                    setEventTypeDropdownOpen(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <label>{t('EventsAdminCityDistrict')} </label>
                            <input type="text" name="citydistrict" value={citydistrict} onChange={(e) => setCityDistrict(e.target.value)} />
                            <label>{t('EventsAdminDescription')} </label>
                            <input type="text" name="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                            <label>{t('EventsAdminDate')} </label>
                            <input type="date" name="date" min={getTomorrowDate()} value={datum} onChange={(e) => {
                                const onlyDate = e.target.value.split("T")[0]; // keep only "YYYY-MM-DD"
                                setDatum(onlyDate);
                            }} />
                            <label>{t('EventsAdminTime')} </label>
                            <input type="text" name="time" value={timeslot} onChange={(e) => setTimeSlot(e.target.value)} />
                            <label>{t('EventsAdminMaxPlaces')} </label>
                            <input type="number" name="maxplaces" value={maxplaces} onChange={(e) => setMaxPlaces(e.target.value)} />
                            <div className="admin-checkbox-row">
                                <label htmlFor="isFreeCheckbox">{t('EventsAdminFreeEvent')}</label>
                                <input
                                    type="checkbox"
                                    id="isFreeCheckbox"
                                    checked={isFree}
                                    onChange={(e) => {
                                        setIsFree(e.target.checked);
                                        if (e.target.checked) setPrice("");
                                    }}
                                />
                            </div>
                            <label>{t('EventsAdminPrice')} </label>
                            <input type="number" step="0.01" min="0" name="price" value={isFree ? '0' : price} onChange={(e) => setPrice(e.target.value)} placeholder="z.B. 14.99" disabled={isFree} />
                            <label>{t('EventsAdminUploadImage')} <p />
                                {t('EventsAdminUploadImageText')}
                            </label>
                            <div className="impressum-content-child">
                                <div className="website-picture">
                                    <input type="file" onChange={handleFileChange} style={{ display: 'none' }} id="fileInput" />
                                    <button onClick={() => document.getElementById('fileInput').click()}>+</button>
                                </div>
                                {selectedFile && <button onClick={handleFileUpload}>{t('Upload')}</button>}
                            </div>
                        </div>
                        <h1>{t('EventsAdminUploadWebsiteImageTitle')}</h1>
                        <div className="admin-content-child">
                            <label>{t('EventsAdminUploadWebsiteImageDescription')} </label>
                            <input type="text" value={websitePicDescription} onChange={(e) => setWebsitePicDescription(e.target.value)} placeholder="z.B. Homepage Hero Bild" />
                            <label>{t('EventsAdminUploadWebsiteImage')} </label>
                            <div className="website-picture">
                                <input
                                    type="file"
                                    onChange={handleWebsiteFileChange}
                                    style={{ display: 'none' }}
                                    id="websiteFileInput"
                                />
                                <button onClick={() => document.getElementById('websiteFileInput').click()}>+</button>
                                {selectedWebsiteFile && (
                                    <button onClick={handleWebsiteFileUpload}>{t('Upload')}</button>
                                )}
                            </div>
                        </div>
                        <h1>{t('EventsAdminUploadWebsiteVideoTitle')}</h1>
                        <div className="admin-content-child">
                            <label>{t('EventsAdminUploadWebsiteVideoDescription')}: </label>
                            <div className="website-picture">
                                <input
                                    type="file"
                                    onChange={handleWebsiteVideoChange}
                                    style={{ display: 'none' }}
                                    id="websiteVideoInput"
                                    accept="video/*" // optional: allow only video files
                                />
                                <button onClick={() => document.getElementById('websiteVideoInput').click()}>+</button>
                                {selectedWebsiteVideo && (
                                    <button onClick={handleWebsiteVideoUpload}>{t('Upload')}</button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <h1 className="matched-events-title"> {t('EventsAdminMatchedEventsTitle')}</h1>
                <div className="events-table-wrapper">
                    <table className="events-table">
                        <thead>
                            <tr>
                                <th>Group ID</th>
                                <th>Group Index</th>
                                <th>Event Name</th>
                                <th>Description</th>
                                <th>Event Type</th>
                                <th>Lokal</th>
                                <th>Address</th>
                                <th>Time</th>
                                <th>Submit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matchedEvents.map((event, index) => (
                                <tr key={event.groupid || `pc-${event.eventid}`}>
                                    <td data-label="Group ID">{event.groupid || "—"}</td>
                                    <td data-label="Group Index">{event.groupindex != null ? event.groupindex : "—"}</td>
                                    <td data-label="Event Name">{event.eventname}</td>
                                    <td data-label="Description">{event.description}</td>
                                    <td data-label="Event Type">{event.eventtype}</td>
                                    {/* Editable Lokal */}
                                    <td data-label="Lokal">
                                        <input
                                            type="text"
                                            value={event.lokal || ""}
                                            onChange={(e) => {
                                                const updated = [...matchedEvents];
                                                updated[index].lokal = e.target.value;
                                                setMatchedEvents(updated);
                                            }}
                                        />
                                    </td>
                                    {/* Editable Address */}
                                    <td data-label="Address">
                                        <input
                                            type="text"
                                            value={event.address || ""}
                                            onChange={(e) => {
                                                const updated = [...matchedEvents];
                                                updated[index].address = e.target.value;
                                                setMatchedEvents(updated);
                                            }}
                                        />
                                    </td>
                                    {/* NOT editable */}
                                    <td data-label="Time">{event.time}</td>
                                    <td data-label="">
                                        <button onClick={() => updateEventRow(event)}>
                                            Submit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div ref={footerRef}>
                    <Footer></Footer>
                </div>
            </>
        </div>
    );
};

export default EventsAdmin;